/**
 * usePushNotifications - Hook for managing browser push notification subscriptions
 */
import { useState, useEffect, useCallback } from 'react';
import { pushNotificationsAPI } from '../lib/api';

// Convert VAPID key from URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permissionState, setPermissionState] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermissionState(Notification.permission);
    }
    
    setLoading(false);
  }, []);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!isSupported) return;

    const init = async () => {
      try {
        // Shared service worker is already registered by lib/pwa.js at bootstrap.
        // Just wait for it to be ready and reuse the registration.
        const registration = await navigator.serviceWorker.ready;
        console.log('Service Worker ready:', registration);

        // Check for existing subscription
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          setSubscription(existingSub);
          setIsSubscribed(true);
        }
      } catch (err) {
        console.error('Service worker registration failed:', err);
        setError('Failed to register service worker');
      }
    };

    init();
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      // Request permission
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission !== 'granted') {
        setError('Notification permission denied');
        return false;
      }

      // Get VAPID public key from server
      const vapidResponse = await pushNotificationsAPI.getVapidKey();
      const vapidPublicKey = vapidResponse.data.publicKey;

      if (!vapidPublicKey) {
        setError('Push notifications not configured on server');
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push manager
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subscriptionJSON = pushSubscription.toJSON();
      await pushNotificationsAPI.subscribe({
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
        user_agent: navigator.userAgent
      });

      setSubscription(pushSubscription);
      setIsSubscribed(true);
      return true;

    } catch (err) {
      console.error('Failed to subscribe:', err);
      setError(err.message || 'Failed to enable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;

    try {
      setLoading(true);
      setError(null);

      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove subscription from server
      await pushNotificationsAPI.unsubscribe(subscription.endpoint);

      setSubscription(null);
      setIsSubscribed(false);
      return true;

    } catch (err) {
      console.error('Failed to unsubscribe:', err);
      setError(err.message || 'Failed to disable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  return {
    isSupported,
    isSubscribed,
    permissionState,
    loading,
    error,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
